import { useCallback, useEffect, useRef, useState } from "react";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import PropTypes from "prop-types";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";

import { API_URL } from "config";

const plainTextFromHtml = (html) => {
  if (!html) return "";
  if (typeof window === "undefined" || !window.DOMParser) return String(html);
  const documentHtml = new window.DOMParser().parseFromString(String(html), "text/html");
  return documentHtml.body.textContent || "";
};

const sanitizeRichHtml = (html) => {
  if (!html) return "";
  if (typeof window === "undefined" || !window.DOMParser) return String(html);

  const parser = new window.DOMParser();
  const documentHtml = parser.parseFromString(String(html), "text/html");
  documentHtml
    .querySelectorAll("script,style,iframe,object,embed,meta,link")
    .forEach((node) => node.remove());
  documentHtml.querySelectorAll("*").forEach((node) => {
    [...node.attributes].forEach((attribute) => {
      const name = attribute.name.toLowerCase();
      const value = String(attribute.value || "").trim().toLowerCase();
      if (
        name.startsWith("on") ||
        ((name === "href" || name === "src" || name === "xlink:href") &&
          value.startsWith("javascript:"))
      ) {
        node.removeAttribute(attribute.name);
      }
    });
  });

  return documentHtml.body.innerHTML;
};

const richContentSx = {
  color: "#66789c",
  fontSize: "0.9rem",
  lineHeight: 1.45,
  overflowWrap: "break-word",
  "& p": { marginTop: 0, marginBottom: "0.45rem" },
  "& table": {
    width: "max-content",
    minWidth: "100%",
    maxWidth: "100%",
    borderCollapse: "collapse",
    borderSpacing: 0,
    tableLayout: "auto",
    margin: "0.5rem 0",
  },
  "& th, & td": {
    minWidth: 60,
    maxWidth: 320,
    border: "1px solid #94a3b8",
    padding: "5px 8px",
    verticalAlign: "top",
    whiteSpace: "pre-wrap",
    overflowWrap: "break-word",
  },
  "& th": {
    backgroundColor: "#f1f5f9",
    color: "#334155",
    fontWeight: 700,
  },
  "& img": {
    display: "block",
    maxWidth: "100%",
    height: "auto",
    margin: "0.5rem 0",
  },
};

const editorSx = {
  border: "1px solid #d2d6da",
  borderRadius: "6px",
  overflow: "hidden",
  backgroundColor: "#fff",
  "& .project-communication-editor-toolbar": {
    display: "flex",
    alignItems: "center",
    gap: 0.5,
    flexWrap: "wrap",
    borderBottom: "1px solid #e2e8f0",
    padding: "5px 7px",
    backgroundColor: "#f8fafc",
  },
  "& .project-communication-editor-toolbar button": {
    minWidth: 26,
    height: 24,
    padding: "0 6px",
    border: "1px solid #cbd5e1",
    borderRadius: "4px",
    backgroundColor: "#fff",
    color: "#334155",
    cursor: "pointer",
    fontSize: "11px",
  },
  "& .project-communication-editor-toolbar button:hover": {
    backgroundColor: "#e0f2fe",
    borderColor: "#7dd3fc",
  },
  "& .project-communication-editor-body": {
    minHeight: 130,
    maxHeight: 220,
    overflow: "auto",
    padding: "8px",
    fontSize: "12px",
    lineHeight: 1.35,
    outline: "none",
    ...richContentSx,
  },
};

const MAX_ATTACHMENTS = 10;
const MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024;

const formatFileSize = (bytes = 0) => {
  const size = Number(bytes) || 0;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

function RichEditor({ value, onChange }) {
  const editorRef = useRef(null);

  useEffect(() => {
    if (
      editorRef.current &&
      editorRef.current.innerHTML !== (value || "") &&
      document.activeElement !== editorRef.current
    ) {
      editorRef.current.innerHTML = value || "";
    }
  }, [value]);

  const syncValue = () => {
    onChange(editorRef.current?.innerHTML || "");
  };

  const runCommand = (command, commandValue = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, commandValue);
    syncValue();
  };

  const createLink = () => {
    const url = window.prompt("Enter URL");
    if (url) runCommand("createLink", url);
  };

  const handlePaste = (event) => {
    const html = event.clipboardData?.getData("text/html");
    if (html) {
      event.preventDefault();
      document.execCommand("insertHTML", false, sanitizeRichHtml(html));
      syncValue();
      return;
    }

    const imageItem = Array.from(event.clipboardData?.items || []).find(
      (item) => item.kind === "file" && String(item.type || "").startsWith("image/")
    );
    if (!imageItem) return;

    const imageFile = imageItem.getAsFile();
    if (!imageFile) return;

    event.preventDefault();
    const selection = window.getSelection();
    const savedRange = selection?.rangeCount ? selection.getRangeAt(0).cloneRange() : null;
    const reader = new window.FileReader();
    reader.onload = () => {
      editorRef.current?.focus();
      if (savedRange && selection) {
        selection.removeAllRanges();
        selection.addRange(savedRange);
      }
      document.execCommand(
        "insertHTML",
        false,
        `<img src="${String(reader.result)}" alt="${imageFile.name || "Pasted image"}">`
      );
      syncValue();
    };
    reader.readAsDataURL(imageFile);
  };

  return (
    <SoftBox sx={editorSx}>
      <SoftBox className="project-communication-editor-toolbar">
        {[
          ["bold", "B", "Bold"],
          ["italic", "I", "Italic"],
          ["underline", "U", "Underline"],
          ["insertUnorderedList", "• List", "Bullet list"],
          ["insertOrderedList", "1. List", "Numbered list"],
        ].map(([command, label, title]) => (
          <button
            key={command}
            type="button"
            title={title}
            onMouseDown={(event) => {
              event.preventDefault();
              runCommand(command);
            }}
          >
            {label}
          </button>
        ))}
        <button
          type="button"
          title="Insert link"
          onMouseDown={(event) => {
            event.preventDefault();
            createLink();
          }}
        >
          Link
        </button>
        <button
          type="button"
          title="Remove formatting"
          onMouseDown={(event) => {
            event.preventDefault();
            runCommand("removeFormat");
          }}
        >
          Clear
        </button>
      </SoftBox>
      <SoftBox
        ref={editorRef}
        className="project-communication-editor-body"
        contentEditable
        suppressContentEditableWarning
        onInput={syncValue}
        onBlur={syncValue}
        onPaste={handlePaste}
        data-placeholder="Type or paste formatted content from Excel, Google Sheets, or Docs"
      />
    </SoftBox>
  );
}

RichEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

RichEditor.defaultProps = {
  value: "",
};

const communicationScopeShape = PropTypes.shape({
  scopeType: PropTypes.oneOf(["project", "country", "vendor"]),
  projectId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  projectDetailId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  vendorCountryWinId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  vendorId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  projectCode: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  projectName: PropTypes.string,
  label: PropTypes.string,
});

export function ProjectCommunicationComposerDialog({ open, type, scope, onClose, onSent, onNotify }) {
  const [activeTab, setActiveTab] = useState(0);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const attachmentInputRef = useRef(null);
  const isMail = type === "mail";
  const isChat = type === "chat";
  const communicationType = isMail ? "mail" : isChat ? "chat" : "comment";
  const defaultSubject = isMail
    ? String(scope?.projectCode || scope?.projectId || "").trim()
    : "";

  const notify = useCallback(
    (message, color = "success") => {
      if (onNotify) onNotify(message, color);
    },
    [onNotify]
  );

  const fetchHistory = useCallback(async () => {
    if (!open || !scope?.projectId) return;

    setHistoryLoading(true);
    try {
      const params = new URLSearchParams({
        type: communicationType,
        scope_type: scope?.scopeType || "project",
      });
      if (scope?.projectDetailId) params.set("project_detail_id", scope.projectDetailId);
      if (scope?.vendorCountryWinId) {
        params.set("vendor_country_win_id", scope.vendorCountryWinId);
      }

      const response = await fetch(
        `${API_URL}/projects/${scope.projectId}/communications?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken") || ""}`,
            Accept: "application/json",
          },
        }
      );
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data?.success === 0) {
        throw new Error(data?.message || "Failed to load communication history.");
      }

      setHistoryItems(Array.isArray(data?.communications) ? data.communications : []);
    } catch (error) {
      setHistoryItems([]);
      notify(error.message || "Failed to load communication history.", "error");
    } finally {
      setHistoryLoading(false);
    }
  }, [
    communicationType,
    notify,
    open,
    scope?.projectDetailId,
    scope?.projectId,
    scope?.scopeType,
    scope?.vendorCountryWinId,
  ]);

  useEffect(() => {
    if (!open) {
      setHistoryItems([]);
      setSubject("");
      setBody("");
      setAttachments([]);
      setEditingItem(null);
      return;
    }

    setActiveTab(0);
    setHistoryItems([]);
    setBody("");
    setAttachments([]);
    setEditingItem(null);
    setSubject(defaultSubject);
  }, [isMail, open, scope?.projectCode, scope?.projectId]);

  useEffect(() => {
    if (open) fetchHistory();
  }, [fetchHistory, open]);

  const handleTabChange = (event, value) => {
    setActiveTab(value);
    if (value === 0) fetchHistory();
  };

  const startEditing = (item) => {
    if (!isMail) return;
    setEditingItem(item);
    setSubject(String(item?.subject || defaultSubject));
    setBody(String(item?.body || ""));
    setAttachments([]);
    setFileInputKey((current) => current + 1);
    setActiveTab(1);
  };

  const cancelEditing = () => {
    setEditingItem(null);
    setSubject(defaultSubject);
    setBody("");
    setAttachments([]);
    setFileInputKey((current) => current + 1);
  };

  const addAttachments = (files) => {
    const selectedFiles = Array.from(files || []);
    const oversizedFile = selectedFiles.find((file) => file.size > MAX_ATTACHMENT_SIZE);
    if (oversizedFile) {
      notify(`${oversizedFile.name} exceeds the 10 MB attachment limit.`, "error");
      return;
    }

    setAttachments((current) => {
      const next = [...current];
      selectedFiles.forEach((file) => {
        const duplicate = next.some(
          (item) =>
            item.name === file.name && item.size === file.size && item.lastModified === file.lastModified
        );
        if (!duplicate && next.length < MAX_ATTACHMENTS) next.push(file);
      });

      if (next.length < current.length + selectedFiles.length) {
        notify(`You can attach up to ${MAX_ATTACHMENTS} unique files.`, "error");
      }
      return next;
    });
  };

  const removeAttachment = (fileIndex) => {
    setAttachments((current) => current.filter((file, index) => index !== fileIndex));
  };

  const submit = async () => {
    const text = plainTextFromHtml(body).trim();
    const hasEmbeddedContent = /<(img|table|hr)\b/i.test(body);
    if (!text && !hasEmbeddedContent) {
      notify(isMail ? "Mail body is required." : isChat ? "Chat message is required." : "Comment is required.", "error");
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("type", communicationType);
      formData.append("scope_type", scope?.scopeType || "project");
      formData.append("body", body);
      if (subject.trim()) formData.append("subject", subject.trim());
      if (scope?.projectDetailId) formData.append("project_detail_id", scope.projectDetailId);
      if (scope?.vendorCountryWinId) formData.append("vendor_country_win_id", scope.vendorCountryWinId);
      if (scope?.vendorId) formData.append("vendor_id", scope.vendorId);
      attachments.forEach((file) => formData.append("attachments[]", file));

      const isEditingMail = isMail && editingItem?.id;
      if (isEditingMail) formData.append("_method", "PATCH");
      const endpoint = isEditingMail
        ? `${API_URL}/projects/${scope?.projectId}/communications/${editingItem.id}`
        : `${API_URL}/projects/${scope?.projectId}/communications`;
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken") || ""}`,
          Accept: "application/json",
        },
        body: formData,
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data?.success === 0) {
        throw new Error(data?.message || "Failed to save communication.");
      }

      notify(
        data?.message ||
          (isMail ? "Mail saved successfully." : isChat ? "Chat saved successfully." : "Comment saved successfully.")
      );
      if (onSent) onSent(data);
      setBody("");
      setAttachments([]);
      setEditingItem(null);
      setSubject(defaultSubject);
      setFileInputKey((current) => current + 1);
      setActiveTab(0);
      await fetchHistory();
    } catch (error) {
      notify(error.message || "Failed to save communication.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isMail ? "Mail" : isChat ? "Chat" : "Comments"}</DialogTitle>
      <DialogContent>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ mb: 2, borderBottom: "1px solid #e2e8f0" }}
        >
          <Tab label="History" />
          <Tab
            label={
              isMail
                ? editingItem
                  ? "Edit Mail"
                  : "Send New Mail"
                : isChat
                ? "Send New Chat"
                : "Add New Comments"
            }
          />
        </Tabs>

        {activeTab === 0 ? (
          <CommunicationTimeline
            items={historyItems}
            isMail={isMail}
            isChat={isChat}
            loading={historyLoading}
            onEdit={isMail ? startEditing : null}
          />
        ) : (
          <>
            {isMail && (
              <SoftBox mb={1}>
                <SoftTypography variant="caption" fontWeight="bold" color="dark">
                  Subject
                </SoftTypography>
                <SoftInput
                  size="small"
                  value={subject}
                  placeholder="Project code"
                  onChange={(event) => setSubject(event.target.value)}
                />
              </SoftBox>
            )}
            <SoftBox mb={1}>
              <SoftTypography variant="caption" fontWeight="bold" color="dark">
                {isMail ? "Mail Body" : isChat ? "Chat Message" : "Comment"}
              </SoftTypography>
              <RichEditor value={body} onChange={setBody} />
            </SoftBox>
            {isMail && (
              <SoftBox>
                <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                  <SoftTypography variant="caption" fontWeight="bold" color="dark">
                    Attachments
                  </SoftTypography>
                  <SoftTypography variant="caption" color="text">
                    {attachments.length}/{MAX_ATTACHMENTS} files · 10 MB each
                  </SoftTypography>
                </SoftBox>
                <input
                  ref={attachmentInputRef}
                  key={fileInputKey}
                  type="file"
                  multiple
                  hidden
                  onChange={(event) => {
                    addAttachments(event.target.files);
                    event.target.value = "";
                  }}
                />
                <SoftBox
                  role="button"
                  tabIndex={0}
                  onClick={() => attachmentInputRef.current?.click()}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") attachmentInputRef.current?.click();
                  }}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => {
                    event.preventDefault();
                    addAttachments(event.dataTransfer.files);
                  }}
                  sx={{
                    border: "1.5px dashed #38bdf8",
                    borderRadius: "10px",
                    background: "linear-gradient(135deg, #f0f9ff 0%, #f8fafc 100%)",
                    px: 2,
                    py: 1.5,
                    textAlign: "center",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover, &:focus": {
                      borderColor: "#0284c7",
                      backgroundColor: "#e0f2fe",
                      boxShadow: "0 4px 14px rgba(14, 165, 233, 0.12)",
                      outline: "none",
                    },
                  }}
                >
                  <Icon sx={{ color: "#0ea5e9", fontSize: "1.6rem !important" }}>cloud_upload</Icon>
                  <SoftTypography variant="button" fontWeight="bold" color="info" display="block">
                    Drop files here or click to browse
                  </SoftTypography>
                  <SoftTypography variant="caption" color="text">
                    Select multiple files now, or add more in another selection
                  </SoftTypography>
                </SoftBox>
                {attachments.length > 0 && (
                  <SoftBox mt={1} display="grid" gap={0.6}>
                    {attachments.map((file, fileIndex) => (
                      <SoftBox
                        key={`${file.name}-${file.size}-${file.lastModified}`}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={1}
                        px={1}
                        py={0.65}
                        borderRadius="8px"
                        sx={{ border: "1px solid #bae6fd", backgroundColor: "#f0f9ff" }}
                      >
                        <SoftBox display="flex" alignItems="center" gap={0.75} minWidth={0}>
                          <Icon sx={{ color: "#0284c7", fontSize: "1.1rem !important" }}>description</Icon>
                          <SoftBox minWidth={0}>
                            <SoftTypography variant="caption" fontWeight="bold" color="dark" display="block" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {file.name}
                            </SoftTypography>
                            <SoftTypography variant="caption" color="text">
                              {formatFileSize(file.size)}
                            </SoftTypography>
                          </SoftBox>
                        </SoftBox>
                        <SoftButton
                          type="button"
                          variant="text"
                          color="error"
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation();
                            removeAttachment(fileIndex);
                          }}
                          sx={{ minWidth: 28, px: 0.5 }}
                        >
                          <Icon sx={{ fontSize: "1rem !important" }}>close</Icon>
                        </SoftButton>
                      </SoftBox>
                    ))}
                  </SoftBox>
                )}
                {editingItem?.attachments?.length > 0 && (
                  <SoftTypography variant="caption" color="text" display="block" mt={0.75}>
                    Existing attachments will remain. You can add more files above.
                  </SoftTypography>
                )}
              </SoftBox>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <SoftButton variant="outlined" color="dark" size="small" onClick={onClose} disabled={submitting}>
          Close
        </SoftButton>
        {activeTab === 1 && editingItem && (
          <SoftButton
            variant="gradient"
            color="warning"
            size="small"
            onClick={cancelEditing}
            disabled={submitting}
          >
            Cancel Edit
          </SoftButton>
        )}
        {activeTab === 1 && (
          <SoftButton variant="gradient" color="info" size="small" onClick={submit} disabled={submitting}>
            {submitting
              ? "Saving..."
              : editingItem
              ? "Update Mail"
              : isMail
              ? "Send Mail"
              : isChat
              ? "Send Chat"
              : "Save Comment"}
          </SoftButton>
        )}
      </DialogActions>
    </Dialog>
  );
}

ProjectCommunicationComposerDialog.propTypes = {
  open: PropTypes.bool,
  type: PropTypes.oneOf(["comment", "mail", "chat"]),
  scope: communicationScopeShape,
  onClose: PropTypes.func.isRequired,
  onSent: PropTypes.func,
  onNotify: PropTypes.func,
};

ProjectCommunicationComposerDialog.defaultProps = {
  open: false,
  type: "comment",
  scope: null,
  onSent: null,
  onNotify: null,
};

const timelineColors = ["#22c55e", "#ef4444", "#0ea5e9", "#f97316", "#8b5cf6"];

function CommunicationTimeline({ items, isMail, isChat, loading, onEdit }) {
  const emptyLabel = isMail ? "mail" : isChat ? "chat" : "comments";
  const iconName = isMail ? "mail" : isChat ? "chat" : "comment";

  if (loading) {
    return (
      <SoftBox display="flex" alignItems="center" justifyContent="center" py={4}>
        <CircularProgress size={24} />
      </SoftBox>
    );
  }

  if (items.length === 0) {
    return (
      <SoftTypography variant="caption">
        No {emptyLabel} found.
      </SoftTypography>
    );
  }

  return (
    <SoftBox sx={{ position: "relative", pl: 4, py: 1 }}>
      <SoftBox
        sx={{
          position: "absolute",
          left: 14,
          top: 8,
          bottom: 8,
          width: "1px",
          backgroundColor: "#cbd5e1",
        }}
      />
      {items.map((item, index) => {
        const color = timelineColors[index % timelineColors.length];

        return (
          <SoftBox key={item.id} sx={{ position: "relative", pb: 2.25 }}>
            <SoftBox
              sx={{
                position: "absolute",
                left: -31,
                top: 2,
                width: 18,
                height: 18,
                borderRadius: "4px",
                backgroundColor: color,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
              }}
            >
              <Icon sx={{ fontSize: "0.78rem !important" }}>{iconName}</Icon>
            </SoftBox>
            <SoftTypography variant="button" fontWeight="bold" color="dark">
              {item.sender_name || "Admin"}
              {item.subject ? ` - ${item.subject}` : ""}
            </SoftTypography>
            <SoftTypography variant="caption" color="text" display="block" mb={0.7}>
              {item.created_at_display || item.created_at || "-"}
            </SoftTypography>
            {isMail && onEdit && (
              <SoftButton
                variant="outlined"
                color="info"
                size="small"
                onClick={() => onEdit(item)}
                sx={{ mb: 0.75, px: 0.8, py: 0.25, minHeight: 0, fontSize: "0.68rem" }}
              >
                <Icon sx={{ fontSize: "0.8rem !important", mr: 0.35 }}>edit</Icon>
                Edit
              </SoftButton>
            )}
            <SoftBox
              sx={{
                ...richContentSx,
                overflowX: "auto",
                width: "100%",
              }}
              dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(item.body) }}
            />
            {isMail && item.attachments?.length > 0 && (
              <SoftBox mt={1}>
                <SoftTypography variant="caption" fontWeight="bold" color="dark" display="block" mb={0.5}>
                  <Icon sx={{ fontSize: "0.9rem !important", mr: 0.35, verticalAlign: "middle" }}>attach_file</Icon>
                  Attachments ({item.attachments.length})
                </SoftTypography>
                <SoftBox display="grid" gridTemplateColumns={{ xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }} gap={0.6}>
                {item.attachments.map((attachment, attachmentIndex) => (
                  <SoftButton
                    key={attachment.id}
                    component="a"
                    href={attachment.url}
                    target="_blank"
                    rel="noreferrer"
                    size="small"
                    color={attachmentIndex % 2 === 0 ? "info" : "success"}
                    variant="outlined"
                    sx={{ px: 1, py: 0.6, minHeight: 0, justifyContent: "flex-start", textTransform: "none", overflow: "hidden" }}
                  >
                    <Icon sx={{ fontSize: "0.95rem !important", mr: 0.5 }}>download</Icon>
                    <SoftBox component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {attachment.name || "Attachment"} · {formatFileSize(attachment.size)}
                    </SoftBox>
                  </SoftButton>
                ))}
                </SoftBox>
              </SoftBox>
            )}
          </SoftBox>
        );
      })}
    </SoftBox>
  );
}

CommunicationTimeline.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      sender_name: PropTypes.string,
      subject: PropTypes.string,
      created_at: PropTypes.string,
      created_at_display: PropTypes.string,
      body: PropTypes.string,
      attachments: PropTypes.arrayOf(PropTypes.object),
    })
  ),
  isMail: PropTypes.bool,
  isChat: PropTypes.bool,
  loading: PropTypes.bool,
  onEdit: PropTypes.func,
};

CommunicationTimeline.defaultProps = {
  items: [],
  isMail: false,
  isChat: false,
  loading: false,
  onEdit: null,
};

export function ProjectCommunicationTimelineDialog({ open, type, row, onClose, onRead, onNotify }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const isMail = type === "mail";
  const isChat = type === "chat";

  const notify = useCallback(
    (message, color = "success") => {
      if (onNotify) onNotify(message, color);
    },
    [onNotify]
  );

  const fetchItems = useCallback(async () => {
    if (!open || !row?.id) return;

    setLoading(true);
    try {
      const headers = {
        Authorization: `Bearer ${localStorage.getItem("authToken") || ""}`,
        Accept: "application/json",
      };
      const response = await fetch(`${API_URL}/vendor-projects/${row.id}/communications?type=${type}`, {
        headers,
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data?.success === 0) {
        throw new Error(data?.message || "Failed to load communications.");
      }

      setItems(Array.isArray(data?.communications) ? data.communications : []);

      const markReadResponse = await fetch(
        `${API_URL}/vendor-projects/${row.id}/communications/mark-read?type=${type}`,
        {
        method: "POST",
        headers,
        }
      );
      const markReadData = await markReadResponse.json().catch(() => ({}));

      if (markReadResponse.ok) {
        window.dispatchEvent(
          new CustomEvent("notification-counts-updated", {
            detail: {
              counts: { total: Number(markReadData?.unread_count || 0) },
              ids: Array.isArray(markReadData?.notification_ids)
                ? markReadData.notification_ids
                : [],
            },
          })
        );
      }

      if (onRead) onRead(type, row.id);
    } catch (error) {
      setItems([]);
      notify(error.message || "Failed to load communications.", "error");
    } finally {
      setLoading(false);
    }
  }, [notify, onRead, open, row?.id, type]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isMail ? "Mail" : isChat ? "Chat" : "Comments"}</DialogTitle>
      <DialogContent>
        <CommunicationTimeline items={items} isMail={isMail} isChat={isChat} loading={loading} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <SoftButton variant="outlined" color="dark" size="small" onClick={onClose}>
          Close
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
}

ProjectCommunicationTimelineDialog.propTypes = {
  open: PropTypes.bool,
  type: PropTypes.oneOf(["comment", "mail", "chat"]),
  row: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  onClose: PropTypes.func.isRequired,
  onRead: PropTypes.func,
  onNotify: PropTypes.func,
};

ProjectCommunicationTimelineDialog.defaultProps = {
  open: false,
  type: "comment",
  row: null,
  onRead: null,
  onNotify: null,
};
